<template>
  <BaseModal modal-content-class="modal_invite">
    <div class="content">
      <form>
        <h1>{{ $_translations.value.modal_invite_title }}</h1>

        <BaseImage
          :src="inviteImage"
          alt="mobile image"
          class="invite_mob_img"
        />

        <p class="invite_txt">
          {{ $_translations.value.modal_invite_description_pt1 }}
          <span>{{ $_translations.value.modal_invite_description_pt2 }}</span>
          {{ $_translations.value.modal_invite_description_pt3 }}
        </p>

        <BaseTabs
          :tabs="tabs"
          :active-tab="activeInviteType"
          @tab:click="setActiveInviteType"
        />

        <div class="tab-contents">
          <div
            id="sms_send"
            :class="['tab-content', {active: activeInviteType === inviteTypeEnum.SMS }]"
          >
            <BasePhoneNumberFormInput
              :model-value="formValues.msisdn.state"
              :label="$_translations.value.modal_invite_input_label_sms"
              :error="formValues.msisdn.validation.error"
              :error-message="$_translations.value[formValues.msisdn.validation.message]"
              @update:modelValue="formValues.msisdn.setState"
            />
          </div>

          <div
            id="email_send"
            :class="['tab-content', {active: activeInviteType === inviteTypeEnum.EMAIL }]"
          >
            <BaseTextFormInput
              :model-value="formValues.email.state"
              :label="$_translations.value.modal_invite_input_label_email"
              :error="formValues.email.validation.error"
              :error-message="$_translations.value[formValues.email.validation.message]"
              @update:modelValue="formValues.email.setState"
            />
          </div>
        </div>

        <BaseButton
          container-class="invite"
          :disabled="submitting"
          @click:button="handleSubmit"
        >
          {{ $_translations.value.modal_invite_button_proceed }}
        </BaseButton>
      </form>
    </div>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseTabs from '@/components/tabs/BaseTabs.vue';
import BaseTextFormInput from '@/components/formInput/BaseTextFormInput.vue';
import BasePhoneNumberFormInput from '@/components/formInput/BasePhoneNumberFormInput.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAuthStore from '@/store/modules/auth.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import useTabHook from '@/hooks/useTab.js';
import { getApiResponseErrorMessage } from '@/plugins/api.js';
import { apiSendFriendInvitation } from '@/services/general.js';
import { apiResponseTypeEnum } from '@/constants/api.js';
import { inviteImage } from '@/constants/image.js';

const inviteTypeEnum = { SMS: 'SMS', EMAIL: 'EMAIL' };

const tabs = [
  { value: inviteTypeEnum.SMS, text: 'modal_invite_tab_sms', elClass: 'left' },
  { value: inviteTypeEnum.EMAIL, text: 'modal_invite_tab_email', elClass: 'right' },
];

const emits = defineEmits(['invite:resolved']);

const authStore = useAuthStore();

const { formValues, isFormInvalid, resetFormErrors } = useFormHook({ msisdn: '', email: '' });
const [submitting, setSubmittingState] = useUILockHook();
const [activeInviteType, setActiveInviteType] = useTabHook(inviteTypeEnum.SMS);

const validateForm = () => {
  if (activeInviteType.value === inviteTypeEnum.SMS) {
    if (!formValues.msisdn.state) {
      formValues.msisdn.setValidation({ error: true, message: 'common_error_input_field_required' });
    }
  } else if (activeInviteType.value === inviteTypeEnum.EMAIL) {
    if (!formValues.email.state) {
      formValues.email.setValidation({ error: true, message: 'common_error_input_field_required' });
    }
  }

  return isFormInvalid();
};

const handleInvitationError = (responseEnum) => {
  if (activeInviteType.value === inviteTypeEnum.SMS) {
    formValues.msisdn.setValidation({
      error: true,
      message: getApiResponseErrorMessage(responseEnum, {
        [apiResponseTypeEnum.MSISDN_INVALID]: 'modal_invite_error_sms_msisdn_invalid',
        [apiResponseTypeEnum.MSISDN_NOT_EXIST]: 'modal_invite_error_sms_msisdn_not_exist',
      }),
    });
  } else if (activeInviteType.value === inviteTypeEnum.EMAIL) {
    formValues.email.setValidation({
      error: true,
      message: getApiResponseErrorMessage(responseEnum, {
        [apiResponseTypeEnum.MSISDN_INVALID]: 'modal_invite_error_email_msisdn_invalid',
        [apiResponseTypeEnum.MSISDN_NOT_EXIST]: 'modal_invite_error_email_msisdn_not_exist',
      }),
    });
  }
};

const handleSubmit = async () => {
  try {
    setSubmittingState(true);

    resetFormErrors();

    const invalid = validateForm();

    if (invalid) {
      setSubmittingState(false);
      return;
    }

    const payload = {
      name: authStore.user.username,
      type: activeInviteType.value,
      input: '',
    };

    if (activeInviteType.value === inviteTypeEnum.SMS) {
      payload.input = formValues.msisdn.state;
    } else if (activeInviteType.value === inviteTypeEnum.EMAIL) {
      payload.input = formValues.email.state;
    }

    await apiSendFriendInvitation({ payload });

    emits('invite:resolved');
  } catch (error) {
    handleInvitationError(error?.responseEnum);
  } finally {
    setSubmittingState(false);
  }
};
</script>
